import * as React from 'react';
import { OnClickOutside } from 'ui/common/on-click-outside';
import { withOpenState, OpenStateInjectedProps } from 'ui/common/enhancers';
import { AddButton, Checkbox, ComponentProps, DateTime, Textarea } from 'ui';
import { MdAdd } from 'react-icons/md';
import { keyCodeTypes } from 'ui/common/key-code-types';
import { Handlers, HotKeys, KeyMap } from 'ui/common/hotkeys';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import { createFormatters, createUTCDate, dateToUTC, parseAsDateUTC } from 'app/infrastructure/data/date';
import styles from './loss-potential-evaluation-tile-view.scss';
import { ComponentDescriptor, DefaultValueFunction } from 'app/infrastructure/ui/types-uischema';
import { defaultValueHandler } from 'app/area/shared/default-value-handler';
import { AddRiskEvaluationInitialValues } from 'app/area/case/features/loss-potential-evaluation/types';
import { tableName } from 'app/area/case/case-data-model';
import { CopyRiskEvaluationInstance, RiskMatrixInstance } from 'app/area/case/components/loss-potential/constants';
import { getEntityTypeFromPath } from 'app/infrastructure/store/entity';
import { DefaultContainerProps } from 'app/infrastructure/redux';
import { deepFindByKey } from 'app/infrastructure/ui/builder/default-values';
import * as caseDataModel from 'app/area/case/case-data-model';
import { ModalSize } from 'app/components/constants';

interface AddLossPotentialModalTranslationsProps {
  textAddItem?: string;
  textAdd?: string;
  textValidFrom?: string;
  textEvaluationDescription?: string;
  textEvaluationHideInReport?: string;
  textEvaluationCopyFromPrevious?: string;
  textEvaluationCopyFromPreviousResidual?: string;
  textAddEvaluationSubtitle?: string;
  textAddResidualEvaluation?: string;
}

export interface AddLossPotentialModalProps extends AddLossPotentialModalTranslationsProps, DefaultContainerProps {
  children?: React.ReactNode;
  locale?: string;
  componentDescriptor?: ComponentDescriptor;
  evaluationValidFromDefaultValue?: { dateRegex: string; dbFunction: number; valueFunction: DefaultValueFunction };
  getUiDefinition?: () => void;
  onCopyRiskEvaluation?: (riskMatrixInstance: number, instanceCopyCmd: number, initialValues: AddRiskEvaluationInitialValues, 
    newWindow?: boolean) => void;
  allowCopyFromOtherInstance?: boolean;
  addButtonStyle?: 'dropdownItem' | 'default';
  allowCopyEvaluationToResidual?: boolean;  
}

interface AddLossPotentialModalState {
  evaluationValidFrom: Date;
  evaluationDescription: string;
  evaluationHideInReport: number;
  evaluationCopyToResidual: number;
  evaluationValidFromVisible?: boolean;
  evaluationDescriptionVisible?: boolean;
  evaluationHideInReportVisible?: boolean;
}
export class AddLossPotentialModalComponent
  extends React.PureComponent<AddLossPotentialModalProps & OpenStateInjectedProps & ComponentProps, AddLossPotentialModalState>  {
  private keyMap: KeyMap;
  private handlers: Handlers;
  public constructor(props: AddLossPotentialModalProps & OpenStateInjectedProps & ComponentProps) {
    super(props);
    this.state = { evaluationDescription: '', evaluationValidFrom: undefined, evaluationHideInReport: 0, evaluationCopyToResidual: 0,
      evaluationValidFromVisible: false,  evaluationDescriptionVisible: false, evaluationHideInReportVisible: false };

    this.keyMap = {
      onEsc: [keyCodeTypes.Esc]
    };

    this.handlers = {
      onEnter: () => this.onAddDocumentItem,
      onEsc: () => this.closeModal()
    };
  }

  private onAddDocumentItem = (copyInstance: number = CopyRiskEvaluationInstance.CopyPrevious) => {
    const { componentDescriptor, onCopyRiskEvaluation, allowCopyEvaluationToResidual } = this.props;
    const entityTypePath = componentDescriptor?.entityTypePath;
    const { evaluationValidFrom, evaluationDescription, evaluationHideInReport, evaluationCopyToResidual } = this.state;
    const entityType = getEntityTypeFromPath(entityTypePath);
    const initialValues: AddRiskEvaluationInitialValues = { 
      validFrom: evaluationValidFrom, description: evaluationDescription, hideInReport: !!evaluationHideInReport  };
    const riskMatrixInstance = entityType === tableName.lossPotentialResidualEvaluation ? 
      RiskMatrixInstance.ResidualLossPotential : RiskMatrixInstance.LossPotential;
    const copyToResidual = !!(allowCopyEvaluationToResidual && evaluationCopyToResidual);

    onCopyRiskEvaluation(riskMatrixInstance, copyInstance, initialValues, copyToResidual);
    this.closeModal();
  };

  private openModal = async () => {
    const { open, getUiDefinition, componentDescriptor } = this.props;
    const lossPotentialEvaluationTbl = caseDataModel.tableName.lossPotentialEvaluation;
    const lossPotentialEvaluation = (caseDataModel.columnName[lossPotentialEvaluationTbl] as any);
    const lossPotentialEvaluationResidualTbl = caseDataModel.tableName.lossPotentialResidualEvaluation;
    const lossPotentialEvaluationResidual = (caseDataModel.columnName[lossPotentialEvaluationResidualTbl] as any);
    const entityType = getEntityTypeFromPath?.(componentDescriptor?.entityTypePath);
    const isResidual = entityType === lossPotentialEvaluationResidualTbl;
    const evaluation = isResidual ? lossPotentialEvaluationResidual : lossPotentialEvaluation;
    try {
      const uiDefinition: any = await getUiDefinition?.();
      const dataTags = deepFindByKey(uiDefinition, 'dataTag');
      
      const evaluationValidFromDefaultValue = dataTags?.filter(dt=>dt.dataTag === evaluation.validFrom && dt.defaultValue)[0]?.defaultValue;
      try {
        const defaultValue = evaluationValidFromDefaultValue && await defaultValueHandler(evaluationValidFromDefaultValue, {});
        this.setState({
          evaluationValidFrom: defaultValue?.value,        
          evaluationValidFromVisible: dataTags?.some(dt=>dt.dataTag === evaluation?.validFrom),
          evaluationDescriptionVisible: dataTags?.some(dt=>dt.dataTag === evaluation?.evaluationDescription),
          evaluationHideInReportVisible: dataTags?.some(dt=>dt.dataTag === evaluation?.hideInReport)
        }, () => open());
        return;
      }
      catch(e) {
        throw new Error('Cannot fetch Evaluation Valid From default value.\n\n' + e);
      }
    }
    catch (e) {
      throw new Error('Cannot fetch ui definition.\n\n' + e);
    }
  };

  private closeModal = () => {
    const { isOpen, close } = this.props;
    if (!isOpen) {
      return;
    }
    this.setState({
      ...this.state,
      ...{
        evaluationValidFrom: this.state.evaluationValidFrom,
        evaluationValidTo: undefined,
        evaluationDescription: '',
        evaluationHideInReport: 0,
        addResidualEvaluation: 0
      }
    }, () => close());
  };

  private onChangeEvaluationDescription = (value: string) => {
    this.setState({ evaluationDescription: value });
  };

  private onChangeEvaluationValidFrom = (value: Date) => {
    this.setState({ evaluationValidFrom: value });
  };

  private onChangeEvaluationHideInReport = (value: number) => {
    this.setState({ evaluationHideInReport: value });
  };

  private onChangeEvaluationAddResidual = (value: number) => {
    this.setState({ evaluationCopyToResidual: value });
  };
 
  private renderAddNewEvaluationContents = () => {
    const { evaluationDescription, evaluationValidFrom, evaluationHideInReport, evaluationCopyToResidual,
      evaluationValidFromVisible, evaluationDescriptionVisible, evaluationHideInReportVisible } = this.state;
    const { locale, textValidFrom, textEvaluationDescription, textEvaluationHideInReport, 
      allowCopyEvaluationToResidual, textAddResidualEvaluation} = this.props;

    const validFrom =
      <DateTime
        componentId={'evaluation-valid-from'}
        className={`${'evaluation-valid-from'}`}
        useTime={false}
        label={{ text: textValidFrom, required: true }}
        value={evaluationValidFrom}
        onChange={this.onChangeEvaluationValidFrom}
        locale={locale}
        parseDate={parseAsDateUTC}
        createDate={createUTCDate}
        cloneDate={dateToUTC}
        {...createFormatters(locale)}
      />;

    const description =
      <Textarea
        componentId={'evaluation-description'}
        className={'evaluation-description'}
        label={{ text: textEvaluationDescription }}
        value={evaluationDescription}
        onChange={this.onChangeEvaluationDescription}
        maxLength={4000}
        rows={2}
      />;

    const hideInReport =
      <Checkbox
        componentId={'evaluation-hide-in-report'}
        className={'evaluation-hide-in-report'}
        label={{ text: textEvaluationHideInReport, visible: true }}
        value={evaluationHideInReport}
        onChange={this.onChangeEvaluationHideInReport}
      />;

      const addResidualEvaluation =
        <Checkbox
          componentId={'add-residual-evaluation'}
          className={'add-residual-evaluation'}
          label={{ text: textAddResidualEvaluation, visible: true }}
          value={evaluationCopyToResidual}
          onChange={this.onChangeEvaluationAddResidual}
        />;

    return (
      <div className='row col-12'>
        {evaluationValidFromVisible && <span className='col-xl-6'>{validFrom}</span>}
        {evaluationDescriptionVisible && <span className='col-xl-8'>{description}</span>}
        {evaluationHideInReportVisible && <span className='col-xl-4'>{hideInReport}</span>}
        {allowCopyEvaluationToResidual && <span className='col-xl-8'>{addResidualEvaluation}</span>}
      </div>
    );
  };

  private handleOnExtraFooterAction = () => {
    return this.onAddDocumentItem(CopyRiskEvaluationInstance.CopyPreviousFromOtherInstance);
  };

  private renderModal() {
    const { onCopyRiskEvaluation, textAddItem, textAdd, textEvaluationCopyFromPrevious, textAddEvaluationSubtitle,
      textEvaluationCopyFromPreviousResidual, allowCopyFromOtherInstance } = this.props;
    const entityTypePath = this.props.componentDescriptor?.entityTypePath;

    const btnExtraFooterActionTitle = !!(allowCopyFromOtherInstance && entityTypePath?.length) ? 
      (entityTypePath?.[entityTypePath?.length - 1] === tableName.lossPotentialEvaluation ? 
      textEvaluationCopyFromPreviousResidual : textEvaluationCopyFromPrevious) : textEvaluationCopyFromPreviousResidual;

    return (
      <ModalWrapper
        headerTitle={textAddItem}
        headerInfo={textAddEvaluationSubtitle}
        btnTitle={textAdd}
        onClose={this.closeModal}
        onDone={this.onAddDocumentItem}
        isExtraFooterActionDisabled={false}
        btnExtraFooterActionTitle={btnExtraFooterActionTitle}
        onExtraFooterAction={allowCopyFromOtherInstance && this.handleOnExtraFooterAction}
        size={ModalSize.large}>
        {onCopyRiskEvaluation && this.renderAddNewEvaluationContents()}
      </ModalWrapper>
    );
  }

  public render() {
    const {
      isOpen,
      textAddItem,
      addButtonStyle,
      onCopyRiskEvaluation
    } = this.props;

    const addButton = addButtonStyle === 'dropdownItem' ? 
      <button type="button" className="dropdown-item" onClick={this.openModal}>{textAddItem}</button> :
      <div className={styles.addEvaluationButton}>
        <AddButton icon={MdAdd} onClick={this.openModal} >{textAddItem}</AddButton>
      </div>;

    return <>
      <OnClickOutside onClickOutside={this.closeModal}>
        <HotKeys keyMap={this.keyMap} handlers={this.handlers}>
          <div>
            {onCopyRiskEvaluation && addButton}
            {isOpen && this.renderModal()}
          </div>
        </HotKeys>
      </OnClickOutside>
    </>;
  }
}

const c = withOpenState()(AddLossPotentialModalComponent);

export { c as AddLossPotentialModal };
